import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateHeader } from "../../slices/header-slice";
import { getPageById } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';
import Animate from '../../components/animate';
import CoreSection from '../../components/core-section';
import CarouselSection from '../../components/carousel-section';
import QnASection from '../../components/qna-section';
import CtaSection from '../../components/cta-section';
import CustomSubmissionForm from '../../components/custom-submission-form';
import ContactForm from '../../components/contact-form';

import './styles.scss';

const CustomPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [pageData, setPageData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isHeroInView, setIsHeroInView] = useState(true);
    const hasFetched = useRef(false);

    useDataLoading('custom-page', isLoading, []);

    const heroRef = useRef(null);

    useEffect(() => {
        const fetchPageData = async () => {
            hasFetched.current = true;
            setIsLoading(true);
            try {
                const response = await getPageById(id);
                setPageData(response.data);
            } catch (error) {
                console.error('Error fetching page data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPageData();
    }, [id]);

    // IntersectionObserver for hero section
    useEffect(() => {
        const heroElement = heroRef.current;
        if (!heroElement || !pageData?.heroImage) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsHeroInView(entry.isIntersecting);
            },
            {
                threshold: 0.1,
                rootMargin: '0px'
            }
        );

        observer.observe(heroElement);

        return () => {
            if (heroElement) {
                observer.unobserve(heroElement);
            }
        };
    }, [pageData]);

    // Update header based on hero visibility
    useEffect(() => {
        if (!pageData) return;

        if (pageData.heroImage) {
            if (isHeroInView) {
                dispatch(updateHeader('transparent'));
            } else {
                dispatch(updateHeader('light'));
            }
        } else {
            dispatch(updateHeader('light'));
        }
    }, [isHeroInView, dispatch, pageData]);

    if (isLoading || !pageData) {
        return null;
    }

    return (
        <div className="custom-page">
            {pageData.heroImage && (
                <div
                    ref={heroRef}
                    className="page-hero"
                    style={{
                        backgroundImage: `url("${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/hero/${pageData?.heroImage?.fileName}&width=1920&height=1080&quality=99&format=webp")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                >
                    <div className="hero-content">
                        <h1>{pageData.header}</h1>
                        {pageData.subheader && <p>{pageData.subheader}</p>}
                    </div>
                </div>
            )}

            {pageData.sections && pageData.sections.length > 0 && (
                <>
                    {pageData.sections
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((section, index) => {
                            if (section.type === 'Core') {
                                return (
                                    <Animate key={section.id}>
                                        <CoreSection section={section} isFirst={index === 0} />
                                    </Animate>
                                );
                            }
                            if (section.type === 'Carousel') {
                                return (
                                    <Animate key={section.id}>
                                        <CarouselSection section={section} />
                                    </Animate>
                                );
                            }
                            if (section.type === 'QnA') {
                                return (
                                    <Animate key={section.id}>
                                        <QnASection section={section} />
                                    </Animate>
                                );
                            }
                            if (section.type === 'Cta') {
                                return (
                                    <Animate key={section.id}>
                                        <CtaSection section={section} />
                                    </Animate>
                                );
                            }
                            return null;
                        })}
                </>
            )}

            {pageData.formType && (
                <Animate>
                    {pageData.formType === 'Custom Submission Form' && <CustomSubmissionForm />}
                    {pageData.formType === 'Contact Form' && <ContactForm />}
                </Animate>
            )}
        </div>
    );
};

export default CustomPage;
